/**
 * Workflow Editor Component
 *
 * Main workflow editor that combines canvas, toolbar, and config panel.
 * Supports real-time execution visualization with step-by-step status updates.
 *
 * Layout:
 * - Top: Header + Toolbar
 * - Middle: Canvas (left) + NodeConfigPanel (right, always visible)
 * - Bottom: ExecutionProgressBar (compact) + ExecutionDrawer (expandable)
 * - Modal: ExecutionHistoryModal (separate)
 */

import React, { useEffect, useCallback, useState } from "react";
import { ReactFlowProvider } from "@xyflow/react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

import { WorkflowCanvas } from "./WorkflowCanvas";
import { WorkflowToolbar } from "./WorkflowToolbar";
import { NodeConfigPanel } from "./NodeConfigPanel";
import { RunWorkflowDialog } from "./RunWorkflowDialog";
import { ExecutionProgressBar } from "./ExecutionProgressBar";
import { ExecutionDrawer } from "./ExecutionDrawer";
import { ExecutionHistoryModal } from "./ExecutionHistoryModal";
import {
  useWorkflowStore,
  useIsDirty,
  useWorkflowMetadata,
  useIsExecuting,
} from "../hooks/useWorkflowStore";
import {
  useWorkflow,
  useCreateWorkflow,
  useUpdateWorkflow,
  usePollExecution,
  useResumeUserRun,
} from "../hooks/useImageWorkflows";
import type {
  Workflow,
  WorkflowExecution,
  StepStatus,
} from "@/types/imageWorkflow";

interface WorkflowEditorProps {
  /** Workflow ID to edit, null for new workflow */
  workflowId?: string | null;
  /** Auto-open execution history with this execution selected */
  initialExecutionId?: string | null;
  /** Callback when workflow is saved */
  onSave?: (workflow: Workflow) => void;
  /** Callback when navigating back */
  onBack?: () => void;
}

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  workflowId,
  initialExecutionId,
  onSave,
  onBack,
}) => {
  const { t } = useI18n();
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);

  // Store actions
  const loadWorkflow = useWorkflowStore((s) => s.loadWorkflow);
  const initNewWorkflow = useWorkflowStore((s) => s.initNewWorkflow);
  const markClean = useWorkflowStore((s) => s.markClean);
  const getDefinition = useWorkflowStore((s) => s.getDefinition);
  const settings = useWorkflowStore((s) => s.settings);
  const nodes = useWorkflowStore((s) => s.nodes);

  // Execution actions
  const startExecution = useWorkflowStore((s) => s.startExecution);
  const selectExecution = useWorkflowStore((s) => s.selectExecution);
  const setNodeExecutionStatus = useWorkflowStore(
    (s) => s.setNodeExecutionStatus,
  );
  const setNodeExecutionResult = useWorkflowStore(
    (s) => s.setNodeExecutionResult,
  );
  const completeExecution = useWorkflowStore((s) => s.completeExecution);

  // Store state
  const isDirty = useIsDirty();
  const metadata = useWorkflowMetadata();
  const isExecuting = useIsExecuting();

  // API hooks
  const { data: existingWorkflow, isLoading: isLoadingWorkflow } = useWorkflow(
    workflowId ?? null,
  );
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const pollExecution = usePollExecution();
  const resumeUserRun = useResumeUserRun();

  // Load workflow on mount or when ID changes
  useEffect(() => {
    if (workflowId && existingWorkflow) {
      loadWorkflow(existingWorkflow);
    } else if (!workflowId) {
      initNewWorkflow();
    }
  }, [workflowId, existingWorkflow, loadWorkflow, initNewWorkflow]);

  // Auto-open history modal when initialExecutionId is provided
  useEffect(() => {
    if (initialExecutionId && existingWorkflow) {
      selectExecution(initialExecutionId);
      setShowHistoryModal(true);
    }
  }, [initialExecutionId, existingWorkflow, selectExecution]);

  // Auto-expand drawer when execution starts
  useEffect(() => {
    if (isExecuting) {
      setIsDrawerExpanded(true);
    }
  }, [isExecuting]);

  // Validate workflow key
  const validateKey = useCallback((key: string): string | null => {
    if (!key || key.trim() === "") {
      return t("imageWorkflows.editor.validation.keyRequired");
    }
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      return t("imageWorkflows.editor.validation.keyInvalid");
    }
    if (key.length > 64) {
      return t("imageWorkflows.editor.validation.keyTooLong");
    }
    return null;
  }, [t]);

  // Handle save
  const handleSave = useCallback(async () => {
    // Validate key before saving
    const keyError = validateKey(metadata.key);
    if (keyError) {
      toast.error(keyError);
      return;
    }

    const definition = getDefinition();

    // Validate: image nodes must have explicit model (no "auto" or empty)
    const imageNodeTypes = ['generate', 'edit', 'fuse'];
    for (const node of (definition.nodes ?? [])) {
      if (imageNodeTypes.includes(node.type)) {
        const model = node.data?.model;
        if (!model || model === 'auto') {
          toast.error(t('imageWorkflows.editor.toast.modelRequired', { node: node.data?.label || node.key }));
          return;
        }
      }
    }

    try {
      if (metadata.id) {
        // Update existing workflow
        const updated = await updateWorkflow.mutateAsync({
          workflowId: metadata.id,
          data: {
            name: metadata.name,
            key: metadata.key,
            description: metadata.description,
            definition,
            prompt_strategy: settings.promptStrategy,
            prompt_config: settings.promptConfig,
            default_storage_mode: settings.storageMode,
          },
        });
        markClean();
        toast.success(t("imageWorkflows.editor.toast.saveSuccess"));
        // Show validation warnings (non-blocking)
        if (updated?.warnings?.length) {
          updated.warnings.forEach(
            (w: { rule: string; node_key: string; message: string }) => {
              toast.warning(`${w.node_key}: ${w.message}`, { duration: 8000 });
            },
          );
        }
        onSave?.(updated);
      } else {
        // Create new workflow
        const created = await createWorkflow.mutateAsync({
          name: metadata.name,
          key: metadata.key,
          description: metadata.description,
          definition,
          prompt_strategy: settings.promptStrategy,
          prompt_config: settings.promptConfig,
          default_storage_mode: settings.storageMode,
        });
        // Update store with new ID
        loadWorkflow(created);
        markClean();
        toast.success(t("imageWorkflows.editor.toast.createSuccess"));
        // Show validation warnings (non-blocking)
        if (created?.warnings?.length) {
          created.warnings.forEach(
            (w: { rule: string; node_key: string; message: string }) => {
              toast.warning(`${w.node_key}: ${w.message}`, { duration: 8000 });
            },
          );
        }
        onSave?.(created);
      }
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(t("imageWorkflows.editor.toast.saveFailed"));
    }
  }, [
    metadata,
    settings,
    getDefinition,
    createWorkflow,
    updateWorkflow,
    markClean,
    loadWorkflow,
    onSave,
    t,
    validateKey,
  ]);

  // Handle run
  const handleRun = useCallback(() => {
    // Save first if dirty
    if (isDirty) {
      toast.info(t("imageWorkflows.editor.toast.saveFirst"));
      return;
    }
    setShowRunDialog(true);
  }, [isDirty, t]);

  // Handle execution start - called when workflow starts executing
  const handleExecutionStart = useCallback(
    (executionId: string) => {
      // Initialize execution tracking
      startExecution(executionId);
      setShowRunDialog(false);
      setIsDrawerExpanded(true);

      // Start polling for updates.
      // onUpdate fires every poll tick — keep it side-effect-free for step UI only.
      // Terminal toast + completeExecution belong in onSuccess so they fire exactly
      // once when polling settles, not while step state is still being mirrored.
      // Wrapped in try/catch so a synchronous throw from .mutate (rare) still
      // releases the executing state — mirrors handleResume's safety net.
      try {
        pollExecution.mutate(
          {
            executionId,
            onUpdate: (execution: WorkflowExecution) => {
              if (execution.user_runs && execution.user_runs.length > 0) {
                const userRun = execution.user_runs[0];
                if (userRun.steps) {
                  for (const step of userRun.steps) {
                    setNodeExecutionStatus(
                      step.node_key,
                      step.status as StepStatus,
                    );
                    setNodeExecutionResult(step.node_key, step);
                  }
                }
              }
            },
          },
          {
            onSuccess: (execution) => {
              completeExecution(
                execution.status,
                execution.error_summary ?? undefined,
              );
              if (execution.status === "completed") {
                toast.success(
                  t("imageWorkflows.editor.toast.executionComplete"),
                );
              } else if (execution.status === "failed") {
                toast.error(t("imageWorkflows.editor.toast.executionFailed"));
              } else if (execution.status === "partial") {
                toast.warning(
                  t("imageWorkflows.editor.toast.executionPartial"),
                );
              } else if (execution.status === "cancelled") {
                toast.info(
                  t("imageWorkflows.editor.toast.executionCancelled"),
                );
              } else {
                console.warn(
                  "[WorkflowEditor] Unexpected terminal status:",
                  execution.status,
                );
                toast.info(
                  t("imageWorkflows.editor.toast.executionPartial"),
                );
              }
            },
            onError: (error) => {
              console.error("[WorkflowEditor] Polling failed:", error);
              const message = error instanceof Error ? error.message : String(error);
              completeExecution("failed", message);
              toast.error(t("imageWorkflows.editor.toast.executionFailed"));
            },
          },
        );
      } catch (error) {
        console.error("[WorkflowEditor] Failed to start polling:", error);
        const message = error instanceof Error ? error.message : String(error);
        completeExecution("failed", message);
        toast.error(t("imageWorkflows.editor.toast.executionFailed"));
      }
    },
    [
      startExecution,
      pollExecution,
      setNodeExecutionStatus,
      setNodeExecutionResult,
      completeExecution,
      t,
    ],
  );

  // Handle back navigation with unsaved changes warning
  const handleBack = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm(
        t("imageWorkflows.editor.unsavedWarning"),
      );
      if (!confirmed) return;
    }
    onBack?.();
  }, [isDirty, onBack, t]);

  // Handle open history modal
  const handleOpenHistory = useCallback(() => {
    setShowHistoryModal(true);
  }, []);

  // Handle resume from failed execution
  const handleResume = useCallback(
    async (executionId: string, userRunId: string, resumeNodeKey: string) => {
      // Close history modal first
      setShowHistoryModal(false);

      // Initialize execution tracking (this will show the execution state on canvas)
      startExecution(executionId);
      setIsDrawerExpanded(true);

      try {
        // Start resume (async, not waiting for completion)
        await resumeUserRun.mutateAsync({
          executionId,
          userRunId,
          resumeFromNodeKey: resumeNodeKey,
          sync: false, // Don't wait - we'll poll for updates
        });

        // Poll for updates — onUpdate mirrors step state, terminal toast in onSuccess
        pollExecution.mutate(
          {
            executionId,
            onUpdate: (execution: WorkflowExecution) => {
              if (execution.user_runs && execution.user_runs.length > 0) {
                const userRun = execution.user_runs[0];
                if (userRun.steps) {
                  for (const step of userRun.steps) {
                    setNodeExecutionStatus(
                      step.node_key,
                      step.status as StepStatus,
                    );
                    setNodeExecutionResult(step.node_key, step);
                  }
                }
              }
            },
          },
          {
            onSuccess: (execution) => {
              completeExecution(
                execution.status,
                execution.error_summary ?? undefined,
              );
              if (execution.status === "completed") {
                toast.success(
                  t("imageWorkflows.editor.toast.resumeComplete"),
                );
              } else if (execution.status === "failed") {
                toast.error(t("imageWorkflows.editor.toast.resumeFailed"));
              } else if (execution.status === "partial") {
                toast.warning(
                  t("imageWorkflows.editor.toast.resumePartial"),
                );
              } else if (execution.status === "cancelled") {
                toast.info(
                  t("imageWorkflows.editor.toast.resumeCancelled"),
                );
              } else {
                console.warn(
                  "[WorkflowEditor] Unexpected resume terminal status:",
                  execution.status,
                );
                toast.info(
                  t("imageWorkflows.editor.toast.resumePartial"),
                );
              }
            },
            onError: (error) => {
              console.error("[WorkflowEditor] Resume polling failed:", error);
              const message = error instanceof Error ? error.message : String(error);
              completeExecution("failed", message);
              toast.error(t("imageWorkflows.editor.toast.resumeFailed"));
            },
          },
        );
      } catch (error) {
        console.error("Resume failed:", error);
        toast.error(t("imageWorkflows.editor.toast.resumeError"));
        const message = error instanceof Error ? error.message : String(error);
        completeExecution("failed", message);
      }
    },
    [
      startExecution,
      resumeUserRun,
      pollExecution,
      setNodeExecutionStatus,
      setNodeExecutionResult,
      completeExecution,
      t,
    ],
  );

  // Loading state
  if (workflowId && isLoadingWorkflow) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-gray-500">{t("imageWorkflows.editor.loading")}</p>
        </div>
      </div>
    );
  }

  const isSaving = createWorkflow.isPending || updateWorkflow.isPending;

  return (
    <ReactFlowProvider>
      <div className="h-full flex flex-col">
        {/* Header with back button */}
        <div className="border-b bg-white px-4 py-2 flex items-center gap-4">
          {onBack && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t("imageWorkflows.editor.back")}
            </button>
          )}
          <h1 className="text-lg font-semibold text-gray-800">
            {metadata.id
              ? t("imageWorkflows.editor.editWorkflow")
              : t("imageWorkflows.editor.newWorkflow")}
          </h1>
          {metadata.version > 0 && (
            <span className="text-xs text-gray-400">v{metadata.version}</span>
          )}
        </div>

        {/* Toolbar */}
        <WorkflowToolbar
          onSave={handleSave}
          onRun={handleRun}
          onHistory={handleOpenHistory}
          isSaving={isSaving}
          isRunning={isExecuting || pollExecution.isPending}
          hasWorkflowId={!!metadata.id}
        />

        {/* Main content - Canvas + Config Panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* Canvas - read-only during execution */}
          <div className="flex-1">
            <WorkflowCanvas readOnly={isExecuting || pollExecution.isPending} />
          </div>

          {/* Config panel - always visible */}
          <NodeConfigPanel />
        </div>

        {/* Bottom execution area */}
        {isDrawerExpanded ? (
          <ExecutionDrawer
            isOpen={isDrawerExpanded}
            onClose={() => setIsDrawerExpanded(false)}
            onOpenHistory={handleOpenHistory}
          />
        ) : (
          <ExecutionProgressBar
            totalNodes={nodes.length}
            isExpanded={isDrawerExpanded}
            onToggleExpand={() => setIsDrawerExpanded(true)}
            onOpenHistory={handleOpenHistory}
          />
        )}

        {/* Run dialog */}
        {metadata.id && (
          <RunWorkflowDialog
            isOpen={showRunDialog}
            workflowId={metadata.id}
            workflowName={metadata.name}
            onClose={() => setShowRunDialog(false)}
            onSuccess={handleExecutionStart}
          />
        )}

        {/* History modal */}
        <ExecutionHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          workflowId={metadata.id}
          onResume={handleResume}
          isResuming={resumeUserRun.isPending}
        />
      </div>
    </ReactFlowProvider>
  );
};

export default WorkflowEditor;
